import { create } from 'zustand';
import { api } from '../lib/api';
import type { Branding } from '../lib/types';

/**
 * Which centre the platform administrator is currently working inside.
 *
 * A super-admin belongs to no organisation, so every tenant-scoped request
 * needs one named. Rather than build a second set of admin screens that would
 * drift from the ones instructors use, the administrator picks a centre and
 * then uses exactly the same screens — the choice is carried on the request,
 * and there is nothing they can do that a teacher could not.
 *
 * Kept in sessionStorage rather than memory so a page reload does not lose the
 * context mid-task, and not in localStorage so a shared machine does not
 * silently resume somebody else's centre a week later.
 */
const KEY = 'target.workspace';

/**
 * That the administrator closed a centre on purpose.
 *
 * On a deployment running one centre, the console opens it by itself — the
 * alternative is a rail full of locked screens that reads as deleted work.
 * But leaving is also a deliberate act, and without remembering it the two
 * fight: "Leave this centre" cleared the choice, the opener saw no centre
 * open and immediately opened the only one there was, and the button looked
 * broken. Remembered for the tab, like the choice itself, and forgotten the
 * moment a centre is opened again.
 */
const LEFT_KEY = 'target.workspace.left';

interface WorkspaceState {
  organizationId: string | null;
  organizationName: string | null;
  /** True while the administrator has closed a centre and not opened another. */
  leftDeliberately: boolean;
  choose: (org: { id: string; name: string } | null) => void;
  restore: () => void;
  /** Drops everything: a different person is about to use this tab. */
  forget: () => void;
}

const read = (): { id: string; name: string } | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as { id: string; name: string }) : null;
  } catch {
    // A private window or blocked storage: work without a remembered choice.
    return null;
  }
};

export const useWorkspace = create<WorkspaceState>((set) => ({
  organizationId: null,
  organizationName: null,
  leftDeliberately: false,

  choose(org) {
    try {
      if (org) {
        sessionStorage.setItem(KEY, JSON.stringify(org));
        sessionStorage.removeItem(LEFT_KEY);
      } else {
        sessionStorage.removeItem(KEY);
        sessionStorage.setItem(LEFT_KEY, '1');
      }
    } catch {
      // Not being able to remember it is survivable; losing the choice on
      // reload is a smaller problem than refusing to make it.
    }
    set({
      organizationId: org?.id ?? null,
      organizationName: org?.name ?? null,
      leftDeliberately: !org,
    });
    // Scoped requests read this on every call, so nothing needs re-fetching
    // beyond whatever screen is about to mount.
    setScopedOrganization(org?.id ?? null);
  },

  restore() {
    const saved = read();
    let left = false;
    try {
      left = sessionStorage.getItem(LEFT_KEY) === '1';
    } catch {
      // Blocked storage: the opener behaves as it did before this existed.
    }
    set({
      organizationId: saved?.id ?? null,
      organizationName: saved?.name ?? null,
      leftDeliberately: left && !saved,
    });
    setScopedOrganization(saved?.id ?? null);
  },

  forget() {
    try {
      sessionStorage.removeItem(KEY);
      sessionStorage.removeItem(LEFT_KEY);
    } catch {
      // Nothing to clear, or nowhere to clear it from.
    }
    set({ organizationId: null, organizationName: null, leftDeliberately: false });
    setScopedOrganization(null);
  },
}));

/**
 * Adds the chosen centre to every request that needs one.
 *
 * The server already accepts `organizationId` on its tenant-scoped endpoints
 * and ignores it for anyone who is not a platform administrator — an
 * instructor naming a different centre gets a 403, not somebody else's data.
 * So this is a client convenience, not the security boundary.
 */
let scopedOrganizationId: string | null = null;

export function setScopedOrganization(id: string | null) {
  scopedOrganizationId = id;
}

api.interceptors.request.use((config) => {
  if (!scopedOrganizationId) return config;

  if (config.method === 'get' || config.method === 'delete') {
    config.params = { organizationId: scopedOrganizationId, ...(config.params ?? {}) };
  } else if (config.data instanceof FormData) {
    if (!config.data.has('organizationId')) config.data.append('organizationId', scopedOrganizationId);
  } else if (config.data && typeof config.data === 'object') {
    config.data = { organizationId: scopedOrganizationId, ...config.data };
  } else if (config.data === undefined) {
    config.params = { organizationId: scopedOrganizationId, ...(config.params ?? {}) };
  }

  return config;
});

export type { Branding };
